/**
 * V2.0.8 — Smart Insert: heuristic recommendation for the wall-click
 * add-opening flow. Given the two adjacent rooms (or single room + side
 * for exterior walls), suggest the door/window type a real architect
 * would default to and a short rationale the UI can surface as a chip.
 *
 * Pure JS, no backend call — runs instantly as the WallAddMenu opens.
 * The recommendation is non-binding: the user can ignore it and pick
 * any type from the regular grid.
 */

// Lowercased "room-name keyword → category" map. Order matters: more
// specific names (e.g. primary bathroom) match before generic ones.
const ROOM_KIND_RULES = [
  // Closets are tiny and bifold/pocket-friendly.
  [/walk-?in\s*closet|wardrobe|closet/, "closet"],
  [/primary\s*(suite|bedroom|bath)/, "primary"],
  [/master\s*(suite|bedroom|bath)/, "primary"],
  [/(en-?suite\s*)?bath(room)?|powder|half\s*bath|wc/, "bath"],
  [/laundry|utility|mud(room)?/, "utility"],
  [/pantry/, "pantry"],
  [/garage/, "garage"],
  [/kitchen|kitchenette/, "kitchen"],
  [/dining|breakfast/, "dining"],
  [/(open\s*)?living|great\s*room|family|den/, "living"],
  [/foyer|entry|entryway|vestibule/, "foyer"],
  [/office|study|library/, "office"],
  [/bedroom|bed\s*\d|nursery|guest/, "bedroom"],
  [/sun(room)?|porch|patio|deck|lanai/, "outdoor"],
  [/stair|hall(way)?|corridor/, "circulation"],
];

function classifyRoom(name) {
  const n = String(name || "").toLowerCase();
  for (const [rx, kind] of ROOM_KIND_RULES) {
    if (rx.test(n)) return kind;
  }
  return "generic";
}

// Tag-set tests (order-independent).
const has = (a, b, x, y) => (a === x && b === y) || (a === y && b === x);

/**
 * Recommendation for an INTERIOR wall (shared between two rooms).
 * Returns {kind, type, label, why}.
 */
export function recommendInteriorOpening(roomAName, roomBName) {
  const A = classifyRoom(roomAName);
  const B = classifyRoom(roomBName);
  // Closets get bifold (shallow) or pocket (deep). We can't infer depth
  // reliably from name alone, so default to bifold which is the more
  // common residential default.
  if (A === "closet" || B === "closet") {
    return { kind: "door", type: "bifold", label: "Bifold", why: "Closet doors don't need clearance" };
  }
  // Bath ↔ bedroom: pocket door is the architectural default (saves the
  // primary-suite floor space + privacy from the swing arc).
  if ((A === "bath" && B === "bedroom") || (B === "bath" && A === "bedroom") ||
      (A === "bath" && B === "primary") || (B === "bath" && A === "primary") ||
      (A === "primary" && B === "primary") ||
      (A === "bath" && B === "bath")) {
    return { kind: "door", type: "pocket", label: "Pocket", why: "Saves bathroom floor space" };
  }
  // Pantry / mudroom: dutch reads warmly and lets the room breathe.
  if (has(A, B, "kitchen", "pantry") || has(A, B, "utility", "kitchen")) {
    return { kind: "door", type: "dutch", label: "Dutch", why: "Classic kitchen-pantry pass-through" };
  }
  // Kitchen ↔ dining/living: usually no door at all, but if the user is
  // explicitly adding one, sliding keeps the rooms feeling connected.
  if (has(A, B, "kitchen", "dining") || has(A, B, "kitchen", "living") ||
      has(A, B, "dining", "living")) {
    return { kind: "door", type: "sliding", label: "Sliding", why: "Open-plan, hide when needed" };
  }
  // Garage entry: single, inswing. The user picks side via swing toggle later.
  if (A === "garage" || B === "garage") {
    return { kind: "door", type: "single", label: "Single Entry", why: "Code requires fire-rated single" };
  }
  // Foyer ↔ anything: single is the canonical interior entry.
  if (A === "foyer" || B === "foyer") {
    return { kind: "door", type: "swing", label: "Swing", why: "Standard interior entry" };
  }
  // Catch-all: a basic swing door.
  return { kind: "door", type: "swing", label: "Swing", why: "Standard interior door" };
}

/**
 * Recommendation for an EXTERIOR wall click. Returns:
 *   {category, type, label, why}
 * where category is "door" | "window" — for outdoor-adjacent rooms (living,
 * primary, dining) we lean toward a glass door; for everything else a
 * window is the better default.
 */
export function recommendExteriorOpening(roomName, side) {
  const R = classifyRoom(roomName);
  // Rear of a primary suite / living room → patio access (french or
  // sliding_glass). Sliding_glass is the safer guess (more common on
  // suburban builds + flush threshold).
  if (side === "rear" && (R === "primary" || R === "living" || R === "dining")) {
    return { category: "door", type: "sliding_glass", label: "Glass Slider", why: "Primary patio access" };
  }
  // Front side of any room → entry door (single) if it's a foyer / living;
  // otherwise a bay window for curb appeal.
  if (side === "front") {
    if (R === "foyer" || R === "living") {
      return { category: "door", type: "single", label: "Single Entry", why: "Front-door placement" };
    }
    if (R === "living" || R === "dining") {
      return { category: "window", type: "bay", label: "Bay Window", why: "Curb appeal + window seat" };
    }
  }
  // Bathrooms get awning windows (privacy + ventilation).
  if (R === "bath") {
    return { category: "window", type: "awning", label: "Awning Window", why: "Privacy + ventilation" };
  }
  // Bedrooms get casement (IRC R310 egress).
  if (R === "bedroom" || R === "primary") {
    return { category: "window", type: "casement", label: "Casement", why: "IRC R310 egress" };
  }
  // Kitchen: sliding window (over sink convention).
  if (R === "kitchen") {
    return { category: "window", type: "sliding", label: "Sliding", why: "Over-sink convention" };
  }
  // Catch-all: double-hung is the traditional safe pick.
  return { category: "window", type: "double_hung", label: "Double-Hung", why: "Traditional default" };
}
